import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

import { DropAreaStyled, RootStyled } from '@/ui/DropArea/styles';
import { DropAreaProps } from '@/ui/DropArea/types';

export const DropArea = ({
  onFilesSelected,
  onFilesRejected,
  children,
  accept,
  maxSize,
  multiple = false,
  disabled = false,
  'aria-label': ariaLabel,
  className,
  buttonRef,
}: DropAreaProps): JSX.Element => {
  const { getRootProps, getInputProps, isDragActive, open, rootRef } = useDropzone({
    accept,
    maxSize,
    multiple,
    disabled,
    // The area is a real <button>, so it already opens on click and on Enter/Space.
    // Letting react-dropzone bind those too would open the picker twice.
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles, fileRejections) => {
      if (acceptedFiles.length) onFilesSelected(acceptedFiles);
      if (fileRejections.length) onFilesRejected?.(fileRejections);
    },
  });

  // react-dropzone needs the root element too, so the caller's ref is fed alongside its own.
  // Not merge-refs: it ships ESM only, and every test rendering the area would have to mock it.
  const setButtonRef = useCallback(
    (element: HTMLButtonElement | null) => {
      (rootRef as React.MutableRefObject<HTMLElement | null>).current = element;

      if (typeof buttonRef === 'function') {
        buttonRef(element);
      } else if (buttonRef) {
        (buttonRef as React.MutableRefObject<HTMLButtonElement | null>).current = element;
      }
    },
    [rootRef, buttonRef],
  );

  // No guard is needed for drops while disabled: react-dropzone binds no drag handlers
  // then, but its preventDropOnDocument default still stops the browser from opening the
  // dropped file and navigating away from the wizard.
  return (
    <RootStyled className={className}>
      <DropAreaStyled
        {...getRootProps({ role: 'button' })}
        ref={setButtonRef}
        type="button"
        $dragActive={isDragActive}
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={open}
      >
        {children}
      </DropAreaStyled>
      {/* react-dropzone supplies the visually-hidden styling and aria wiring for this input */}
      <input {...getInputProps()} />
    </RootStyled>
  );
};
